import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { listPublishedPhotographers, type PhotographerOption } from "@/lib/admin-api";
import {
  addPick,
  fetchAdminPicksForWeek,
  fetchAllPickedSources,
  removePick,
  removePicks,
  updatePickPositions,
  type WeekPickRow,
} from "@/lib/selected-week-picks";
import { getISOWeek } from "@/lib/selected-weeks";
import { transformedImageUrl } from "@/lib/image-url";

export const Route = createFileRoute("/admin/selected")({
  head: () => ({ meta: [{ title: "Selected, Admin" }] }),
  component: AdminSelected,
});

function AdminSelected() {
  const today = useMemo(() => getISOWeek(new Date()), []);
  const [year, setYear] = useState(today.year);
  const [week, setWeek] = useState(today.week);
  const [photographers, setPhotographers] = useState<PhotographerOption[]>([]);
  const [picks, setPicks] = useState<WeekPickRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<string[]>([]);
  const [usedByWeek, setUsedByWeek] = useState<Map<string, { year: number; week: number }>>(new Map());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [pickSelection, setPickSelection] = useState<string[]>([]);
  const [confirmRemove, setConfirmRemove] = useState<{
    mode: "single" | "bulk" | "all";
    id?: string;
    count?: number;
  } | null>(null);

  useEffect(() => {
    listPublishedPhotographers().then(setPhotographers).catch((e) => setError(e.message));
  }, []);

  const reload = () =>
    Promise.all([fetchAdminPicksForWeek(year, week), fetchAllPickedSources()])
      .then(([rows, used]) => { setPicks(rows); setUsedByWeek(used); })
      .catch((e) => setError(e.message));

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year, week]);

  // Drop pick-selections that no longer exist after a reload / week change.
  useEffect(() => {
    const ids = new Set(picks.map((p) => p.id));
    setPickSelection((cur) => cur.filter((id) => ids.has(id)));
  }, [picks]);

  const pickedUrls = useMemo(
    () => new Set(picks.flatMap((p) => [p.source_url, p.image_url].filter(Boolean) as string[])),
    [picks],
  );
  // Map each gallery image URL to its owning photographer.
  const urlOwner = useMemo(() => {
    const m = new Map<string, PhotographerOption>();
    for (const p of photographers) for (const url of p.gallery_urls) m.set(url, p);
    return m;
  }, [photographers]);

  function usedElsewhere(url: string): { year: number; week: number } | null {
    const slot = usedByWeek.get(url);
    if (!slot) return null;
    if (slot.year === year && slot.week === week) return null;
    return slot;
  }

  function toggleSelection(url: string) {
    if (usedElsewhere(url)) return;
    setSelection((cur) => cur.includes(url) ? cur.filter((u) => u !== url) : [...cur, url]);
  }

  function toggleExpanded(id: string) {
    setExpanded((cur) => {
      const next = new Set(cur);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function togglePick(id: string) {
    setPickSelection((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  }

  async function onAddSelected() {
    const allowed = selection.filter((u) => !usedElsewhere(u) && urlOwner.has(u));
    if (allowed.length === 0) return;
    setBusy(true); setError(null);
    try {
      for (let i = 0; i < allowed.length; i++) {
        const owner = urlOwner.get(allowed[i])!;
        await addPick({
          year, week,
          photographerDbId: owner.id,
          image_url: allowed[i],
          position: picks.length + i,
        });
      }
      setSelection([]);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Fehler");
    } finally { setBusy(false); }
  }

  async function onRemove(id: string) {
    setConfirmRemove({ mode: "single", id });
  }

  async function onRemoveSelectedPicks(ids: string[]) {
    if (ids.length === 0) return;
    const isAll = ids.length === picks.length;
    setConfirmRemove({ mode: isAll ? "all" : "bulk", count: ids.length });
  }

  async function executeRemove() {
    if (!confirmRemove) return;
    setBusy(true); setError(null);
    try {
      if (confirmRemove.mode === "single" && confirmRemove.id) {
        await removePick(confirmRemove.id);
      } else {
        const ids = confirmRemove.mode === "all"
          ? picks.map((p) => p.id)
          : pickSelection;
        if (ids.length > 0) {
          await removePicks(ids);
          setPickSelection([]);
        }
      }
      setConfirmRemove(null);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Fehler");
    } finally {
      setBusy(false);
    }
  }

  async function movePick(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= picks.length) return;
    const next = picks.slice();
    [next[index], next[target]] = [next[target], next[index]];
    setPicks(next);
    setBusy(true); setError(null);
    try {
      await updatePickPositions(next.map((p, i) => ({ id: p.id, position: i })));
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Fehler");
    } finally { setBusy(false); }
  }

  return (
    <div>
      <div className="eyebrow text-muted-foreground">Selected</div>
      <h1 className="serif text-3xl md:text-4xl mt-3">Wochenweise Freigabe</h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-xl">
        Wähle pro Kalenderwoche Bilder aus den Galerien deiner Fotografen aus.
        Nur freigegebene Bilder erscheinen im Selected-Bereich.
      </p>

      <div className="mt-8 flex flex-wrap items-end gap-4 border border-border p-4">
        <label className="block">
          <div className="eyebrow text-muted-foreground mb-1">Jahr</div>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value || "0", 10))}
            className="w-28 border border-input bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <div className="eyebrow text-muted-foreground mb-1">Kalenderwoche</div>
          <input
            type="number"
            min={1}
            max={53}
            value={week}
            onChange={(e) => setWeek(parseInt(e.target.value || "0", 10))}
            className="w-24 border border-input bg-background px-3 py-2 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={() => { setYear(today.year); setWeek(today.week); }}
          className="eyebrow border border-foreground px-3 py-2 text-xs"
        >Aktuelle KW</button>
        <div className="text-xs text-muted-foreground ml-auto">
          {picks.length} Bild{picks.length === 1 ? "" : "er"} freigegeben
        </div>
      </div>

      {error && <div className="mt-4 text-sm text-destructive">{error}</div>}

      <section className="mt-10">
        <h2 className="serif text-xl mb-4">Aktuelle Auswahl · KW {week} / {year}</h2>
        {picks.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Bilder freigegeben.</p>
        ) : (
          <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              type="button"
              disabled={busy}
              onClick={() => setPickSelection(picks.map((p) => p.id))}
              className="eyebrow text-xs border border-border px-3 py-2 hover:border-foreground disabled:opacity-40"
            >Alle auswählen</button>
            <button
              type="button"
              disabled={busy || pickSelection.length === 0}
              onClick={() => setPickSelection([])}
              className="eyebrow text-xs border border-border px-3 py-2 hover:border-foreground disabled:opacity-40"
            >Auswahl leeren</button>
            <button
              type="button"
              disabled={busy || pickSelection.length === 0}
              onClick={() => onRemoveSelectedPicks(pickSelection)}
              className="eyebrow text-xs border border-destructive text-destructive px-3 py-2 disabled:opacity-40"
            >{busy ? "Wird entfernt …" : `Auswahl entfernen (${pickSelection.length})`}</button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onRemoveSelectedPicks(picks.map((p) => p.id))}
              className="eyebrow text-xs border border-destructive text-destructive px-3 py-2 disabled:opacity-40 ml-auto"
            >Ganze KW entfernen</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {picks.map((p, i) => (
              <div
                key={p.id}
                className={`relative group ${pickSelection.includes(p.id) ? "ring-2 ring-destructive ring-offset-2 ring-offset-background" : ""}`}
              >
                <img
                  src={transformedImageUrl(p.image_url, { width: 400, quality: 65, resize: "contain" })}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    const img = e.currentTarget;
                    if (img.src !== p.image_url) img.src = p.image_url;
                  }}
                  className="w-full aspect-square object-contain bg-muted"
                />
                <label className="absolute bottom-1 right-1 flex items-center gap-1 bg-background/90 border border-border px-1.5 py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pickSelection.includes(p.id)}
                    onChange={() => togglePick(p.id)}
                    disabled={busy}
                    className="h-3.5 w-3.5"
                  />
                  <span className="eyebrow text-[10px]">Auswählen</span>
                </label>
                <div className="absolute top-1 left-1 flex gap-1">
                  <button
                    type="button"
                    disabled={busy || i === 0}
                    onClick={() => movePick(i, -1)}
                    title="Nach vorne"
                    className="eyebrow bg-background/90 border border-foreground px-2 py-1 text-[10px] disabled:opacity-40"
                  >←</button>
                  <button
                    type="button"
                    disabled={busy || i === picks.length - 1}
                    onClick={() => movePick(i, 1)}
                    title="Nach hinten"
                    className="eyebrow bg-background/90 border border-foreground px-2 py-1 text-[10px] disabled:opacity-40"
                  >→</button>
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onRemove(p.id)}
                  className="absolute top-1 right-1 eyebrow bg-background/90 border border-destructive text-destructive px-2 py-1 text-[10px]"
                >Entfernen</button>
                <div className="absolute bottom-1 left-1 eyebrow text-[10px] bg-background/80 px-1.5 py-0.5">{i + 1}</div>
              </div>
            ))}
          </div>
          </>
        )}
      </section>

      <section className="mt-12">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="serif text-xl">Verfügbare Fotos</h2>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setExpanded(new Set(photographers.map((p) => p.id)))}
              className="eyebrow text-xs border border-border px-3 py-2 hover:border-foreground"
            >Alle ausklappen</button>
            <button
              type="button"
              onClick={() => setExpanded(new Set())}
              className="eyebrow text-xs border border-border px-3 py-2 hover:border-foreground"
            >Alle einklappen</button>
          </div>
        </div>

        {selection.length > 0 && (
          <div className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3 mb-4 border border-foreground bg-background p-3">
            <div className="eyebrow text-muted-foreground">{selection.length} Bild{selection.length === 1 ? "" : "er"} ausgewählt</div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSelection([])}
                disabled={busy}
                className="eyebrow text-xs border border-border px-3 py-2 hover:border-foreground"
              >Auswahl leeren</button>
              <button
                type="button"
                onClick={onAddSelected}
                disabled={busy || selection.length === 0}
                className="eyebrow text-xs border border-foreground bg-foreground text-background px-3 py-2 disabled:opacity-40"
              >{busy ? "Wird hinzugefügt …" : `Auswahl freigeben (${selection.length})`}</button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {photographers.map((p) => {
            const isOpen = expanded.has(p.id);
            const selectedCount = p.gallery_urls.filter((u) => selection.includes(u)).length;
            return (
              <div key={p.id} className="border border-border">
                <button
                  type="button"
                  onClick={() => toggleExpanded(p.id)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40"
                >
                  <span className="serif text-base">
                    {p.name}
                    <span className="eyebrow text-xs text-muted-foreground ml-3">
                      {p.gallery_urls.length} Bild{p.gallery_urls.length === 1 ? "" : "er"}
                      {selectedCount > 0 ? ` · ${selectedCount} ausgewählt` : ""}
                    </span>
                  </span>
                  <span className="eyebrow text-xs text-muted-foreground">{isOpen ? "▲" : "▼"}</span>
                </button>
                {isOpen && (
                  <div className="border-t border-border p-4">
                    {p.gallery_urls.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Keine Galerie-Bilder.</p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {p.gallery_urls.map((url) => {
                          const isSelected = selection.includes(url);
                          const otherWeek = usedElsewhere(url);
                          return (
                            <button
                              key={url}
                              type="button"
                              disabled={busy || !!otherWeek}
                              onClick={() => toggleSelection(url)}
                              className={`relative block w-full transition-opacity ${
                                otherWeek
                                  ? "cursor-not-allowed"
                                  : isSelected
                                    ? "ring-2 ring-foreground ring-offset-2 ring-offset-background"
                                    : "hover:opacity-80"
                              }`}
                            >
                              <img
                                src={transformedImageUrl(url, { width: 400, quality: 65, resize: "contain" })}
                                alt=""
                                loading="lazy"
                                decoding="async"
                                onError={(e) => {
                                  const img = e.currentTarget;
                                  if (img.src !== url) { img.srcset = ""; img.src = url; }
                                }}
                                className={`w-full aspect-square object-contain bg-muted ${otherWeek ? "opacity-40" : ""}`}
                              />
                              {otherWeek ? (
                                <span className="absolute top-2 right-2 eyebrow text-[10px] bg-background/90 border border-foreground px-2 py-1">Schon in KW {otherWeek.week} / {otherWeek.year}</span>
                              ) : pickedUrls.has(url) && (
                                <span className="absolute top-2 right-2 eyebrow text-[10px] bg-background/90 border border-foreground px-2 py-1">✓ Bereits freigegeben</span>
                              )}
                              {isSelected && (
                                <span className="absolute top-2 left-2 eyebrow text-[10px] bg-foreground text-background px-2 py-1">✓ Ausgewählt</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {confirmRemove && (
        <div
          className="fixed inset-0 z-50 bg-foreground/40 flex items-center justify-center p-4"
          onClick={() => !busy && setConfirmRemove(null)}
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-background border border-border w-full max-w-md p-6">
            <div className="eyebrow text-muted-foreground">Entfernen bestätigen</div>
            <h2 className="serif text-2xl mt-2">
              {confirmRemove.mode === "single"
                ? "Bild entfernen"
                : confirmRemove.mode === "all"
                  ? "Ganze KW entfernen"
                  : `${confirmRemove.count} Bilder entfernen`}
            </h2>
            <p className="text-sm text-muted-foreground mt-3">
              {confirmRemove.mode === "single"
                ? "Dieses Bild wird aus der aktuellen Wochenauswahl entfernt."
                : confirmRemove.mode === "all"
                  ? "Alle Bilder der aktuellen Kalenderwoche werden entfernt."
                  : "Die ausgewählten Bilder werden aus der aktuellen Wochenauswahl entfernt."}
            </p>
            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <button
                disabled={busy}
                onClick={() => setConfirmRemove(null)}
                className="eyebrow border border-border px-4 py-2 disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                disabled={busy}
                onClick={executeRemove}
                className="eyebrow bg-destructive text-destructive-foreground px-4 py-2 disabled:opacity-50"
              >
                {busy ? "Entferne …" : "Bestätigen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}